import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, Outlet } from "react-router-dom";
import { AuthProvider, useAuth } from "@/components/AuthProvider";
import { CartProvider } from "@/components/CartProvider";
import { LanguageProvider } from "@/components/LanguageProvider";
import { Loader2 } from "lucide-react";
import { HelmetProvider } from "react-helmet-async";

// Lazy Load Public Pages
const Home = lazy(() => import("./pages/public/Home"));
const About = lazy(() => import("./pages/public/About"));
const Contact = lazy(() => import("./pages/public/Contact"));
const Products = lazy(() => import("./pages/public/Products"));
const DattesMedjool = lazy(() => import("./pages/public/DattesMedjool"));
const VenteEnGros = lazy(() => import("./pages/public/VenteEnGros"));
const SingleProduct = lazy(() => import("./pages/public/SingleProduct"));
const Cart = lazy(() => import("./pages/public/Cart"));
const Checkout = lazy(() => import("./pages/public/Checkout"));
const Success = lazy(() => import("./pages/public/Success"));
const Auth = lazy(() => import("./pages/public/Auth"));
const AccessDenied = lazy(() => import("./pages/public/AccessDenied"));
const Privacy = lazy(() => import("./pages/public/Privacy"));
const Terms = lazy(() => import("./pages/public/Terms"));
const ShippingPolicy = lazy(() => import("./pages/public/ShippingPolicy"));
const Blog = lazy(() => import("./pages/public/Blog"));
const SingleBlog = lazy(() => import("./pages/public/SingleBlog"));
const NotFound = lazy(() => import("./pages/public/NotFound"));

// Lazy Load Admin Pages
const Dashboard = lazy(() => import("./pages/admin/Dashboard"));
const Statistics = lazy(() => import("./pages/admin/Statistics"));
const AdminCategories = lazy(() => import("./pages/admin/Categories"));
const AdminProducts = lazy(() => import("./pages/admin/Products"));
const Sells = lazy(() => import("./pages/admin/Sells"));
const Clients = lazy(() => import("./pages/admin/Clients"));
const Contacts = lazy(() => import("./pages/admin/Contacts"));
const Wholesale = lazy(() => import("./pages/admin/Wholesale"));
const Reviews = lazy(() => import("./pages/admin/Reviews"));
const Users = lazy(() => import("./pages/admin/Users"));
const AdminBlog = lazy(() => import("./pages/admin/Blog"));
const Legal = lazy(() => import("./pages/admin/Legal"));
const Settings = lazy(() => import("./pages/admin/Settings"));

const queryClient = new QueryClient();

// Loading Fallback
const PageLoader = () => (
  <div className="h-screen w-full flex items-center justify-center bg-background">
    <Loader2 className="w-10 h-10 animate-spin text-primary" />
  </div>
);

// Map route paths to permission keys
const ROUTE_PERMISSIONS: Record<string, string> = {
  "/admin/dashboard": "dashboard",
  "/admin/statistics": "statistics",
  "/admin/categories": "categories",
  "/admin/products": "products",
  "/admin/sells": "sells",
  "/admin/clients": "clients",
  "/admin/contacts": "contacts",
  "/admin/wholesale": "wholesale",
  "/admin/reviews": "reviews",
  "/admin/blog": "blog",
  "/admin/legal": "legal",
  "/admin/users": "users",
  "/admin/settings": "settings",
};

// Admin Routes Order (for redirection priority)
const ADMIN_ROUTES_ORDER = [
  { path: "/admin/dashboard", key: "dashboard" },
  { path: "/admin/statistics", key: "statistics" },
  { path: "/admin/categories", key: "categories" },
  { path: "/admin/products", key: "products" },
  { path: "/admin/sells", key: "sells" },
  { path: "/admin/clients", key: "clients" },
  { path: "/admin/contacts", key: "contacts" },
  { path: "/admin/wholesale", key: "wholesale" },
  { path: "/admin/reviews", key: "reviews" },
  { path: "/admin/blog", key: "blog" },
  { path: "/admin/legal", key: "legal" },
  { path: "/admin/users", key: "users" },
  { path: "/admin/settings", key: "settings" },
];


const ProtectedRoot = () => {
  const { isAuthenticated, isLoading } = useAuth();

  // Show loading only while checking authentication
  if (isLoading) {
    return (
      <div className="h-screen w-full flex items-center justify-center bg-background">
        <Loader2 className="w-10 h-10 animate-spin text-primary" />
      </div>
    );
  }

  // Redirect to auth if not authenticated
  if (!isAuthenticated) {
    return <Navigate to="/auth" replace />;
  }

  // User is authenticated, show admin routes
  return <Outlet />;
};

const ProtectedRoute = ({ children, path }: { children: React.ReactNode, path: string }) => {
  const { profile } = useAuth();

  if (!profile) return null; // Should be handled by ProtectedRoot, but for safety

  // Super Admin has access to everything
  if (profile.role === "super_admin") {
    return <>{children}</>;
  }

  // Check specific permission
  const requiredPermission = ROUTE_PERMISSIONS[path];
  if (requiredPermission && profile.permissions[requiredPermission]) {
    return <>{children}</>;
  }

  // No access -> Try to find the first allowed page
  const firstAllowed = ADMIN_ROUTES_ORDER.find(route => profile.permissions[route.key]);

  if (firstAllowed) {
    return <Navigate to={firstAllowed.path} replace />;
  }

  // No allowed pages at all
  return <Navigate to="/access-denied" replace />;
};

const App = () => (
  <HelmetProvider>
    <QueryClientProvider client={queryClient}>
      <TooltipProvider>
        <Toaster />
        <Sonner />
        <BrowserRouter>
          <AuthProvider>
            <LanguageProvider>
              <CartProvider>
                <Suspense fallback={<PageLoader />}>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/notre-histoire" element={<About />} />
                    <Route path="/contactez-nous" element={<Contact />} />
                    <Route path="/boutique" element={<Products />} />
                    <Route path="/dattes-medjool" element={<DattesMedjool />} />
                    <Route path="/vente-en-gros" element={<VenteEnGros />} />
                    <Route path="/boutique/:slug" element={<SingleProduct />} />
                    <Route path="/cart" element={<Cart />} />
                    <Route path="/checkout" element={<Checkout />} />
                    <Route path="/success" element={<Success />} />
                    <Route path="/auth" element={<Auth />} />
                    <Route path="/access-denied" element={<AccessDenied />} />
                    <Route path="/privacy" element={<Privacy />} />
                    <Route path="/terms" element={<Terms />} />
                    <Route path="/shipping-policy" element={<ShippingPolicy />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:id" element={<SingleBlog />} />

                    {/* Admin Routes */}
                    <Route element={<ProtectedRoot />}>
                      <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />

                      <Route path="/admin/dashboard" element={
                        <ProtectedRoute path="/admin/dashboard"><Dashboard /></ProtectedRoute>
                      } />
                      <Route path="/admin/statistics" element={
                        <ProtectedRoute path="/admin/statistics"><Statistics /></ProtectedRoute>
                      } />
                      <Route path="/admin/categories" element={
                        <ProtectedRoute path="/admin/categories"><AdminCategories /></ProtectedRoute>
                      } />
                      <Route path="/admin/products" element={
                        <ProtectedRoute path="/admin/products"><AdminProducts /></ProtectedRoute>
                      } />
                      <Route path="/admin/sells" element={
                        <ProtectedRoute path="/admin/sells"><Sells /></ProtectedRoute>
                      } />
                      <Route path="/admin/clients" element={
                        <ProtectedRoute path="/admin/clients"><Clients /></ProtectedRoute>
                      } />
                      <Route path="/admin/contacts" element={
                        <ProtectedRoute path="/admin/contacts"><Contacts /></ProtectedRoute>
                      } />
                      <Route path="/admin/wholesale" element={
                        <ProtectedRoute path="/admin/wholesale"><Wholesale /></ProtectedRoute>
                      } />
                      <Route path="/admin/reviews" element={
                        <ProtectedRoute path="/admin/reviews"><Reviews /></ProtectedRoute>
                      } />
                      <Route path="/admin/blog" element={
                        <ProtectedRoute path="/admin/blog"><AdminBlog /></ProtectedRoute>
                      } />
                      <Route path="/admin/legal" element={
                        <ProtectedRoute path="/admin/legal"><Legal /></ProtectedRoute>
                      } />
                      <Route path="/admin/users" element={
                        <ProtectedRoute path="/admin/users"><Users /></ProtectedRoute>
                      } />
                      <Route path="/admin/settings" element={
                        <ProtectedRoute path="/admin/settings"><Settings /></ProtectedRoute>
                      } />
                    </Route>

                    {/* Catch-all route - must be last */}
                    <Route path="*" element={<NotFound />} />
                  </Routes>
                </Suspense>
              </CartProvider>
            </LanguageProvider>
          </AuthProvider>
        </BrowserRouter>
      </TooltipProvider>
    </QueryClientProvider>
  </HelmetProvider>
);

export default App;
